import { useLocation, Navigate } from 'react-router-dom';
import { useFeatures } from '../contexts/FeatureContext';
import { Navbar } from './Navbar';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * FeatureGate - Wraps routes to check if they're enabled via feature flags
 * If the feature is disabled, shows a maintenance page
 */
const FeatureGate = ({ children }) => {
  const location = useLocation();
  const { isRouteEnabled, getBlockingFeature, loading } = useFeatures();
  
  // Skip checking while features are loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-lime text-xl font-mono">Loading...</div>
      </div>
    );
  }
  
  // Check if current route is enabled
  const routeEnabled = isRouteEnabled(location.pathname);
  
  if (!routeEnabled) {
    const blockingFeature = getBlockingFeature(location.pathname);
    const featureDisplayName = blockingFeature 
      ? blockingFeature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      : 'This feature';
    
    return (
      <div className="min-h-screen bg-[#1a1a1a]">
        <Navbar />
        <div className="max-w-lg mx-auto px-4 py-20">
          <div className="bg-gray-900/80 border border-yellow-500/30 rounded-2xl p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-yellow-500/20 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-10 h-10 text-yellow-400" />
            </div>
            
            <h1 className="text-2xl font-bold text-white  mb-3">
              Feature Unavailable
            </h1>
            
            <p className="text-muted  mb-6">
              <span className="text-yellow-400 font-semibold">{featureDisplayName}</span> is currently disabled by the platform administrator.
            </p>
            
            <div className="bg-gray-800/50 rounded-lg p-4 mb-6">
              <p className="text-sm text-muted">
                This feature may be temporarily unavailable for maintenance or updates. 
                Please check back later or contact support for more information.
              </p>
            </div>
            
            <div className="flex gap-3 justify-center">
              <Button 
                onClick={() => window.history.back()} 
                className="btn-secondary"
              >
                Go Back
              </Button>
              <Button 
                onClick={() => window.location.href = '/dashboard'} 
                className="btn-primary"
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return children;
};

export default FeatureGate;
